/**
 * Everything you can do about a player, in one menu.
 *
 * Shared by every right-click on a person and by the three dots on the profile
 * card, so the same person offers the same actions wherever you find them.
 * Grouped with rules between: who they are, the party, then the things you
 * would rather not need.
 */

import { useCallback, useState } from 'react';
import { copyText } from '@/lib/clipboard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { GameMark } from './GameMark';
import { Introduce } from './FriendLists';
import { Select } from './Select';
import { useContextMenu, type MenuItem } from './Menu';
import { Button, Field, Modal, Textarea, useConfirm, useToast } from './ui';

export type Relation = { status: string; actorId: string; id: string } | null;

const REASONS = [
  ['harassment', 'Harassment or abuse'],
  ['spam', 'Spam or advertising'],
  ['cheating', 'Cheating'],
  ['impersonation', 'Impersonation'],
  ['inappropriate', 'Inappropriate content'],
  ['other', 'Something else']
] as const;

export function useUserMenu(
  user: PublicUser,
  relation?: Relation,
  opts?: { onNavigate?: () => void }
) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [reporting, setReporting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [noting, setNoting] = useState(false);
  const [introducing, setIntroducing] = useState(false);

  const leave = opts?.onNavigate;

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['friends'] });
    await queryClient.invalidateQueries({ queryKey: ['user', user.username] });
  }, [queryClient, user.username]);

  const run = useCallback(async (path: string, body?: Record<string, unknown>, note?: string) => {
    try {
      await api.post(path, body);
      if (note) toast(note, 'good');
      await refresh();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }, [refresh, toast]);

  const copy = useCallback((text: string) => {
    // Says what actually happened - it used to claim success even where the
    // clipboard API does not exist at all.
    void copyText(text).then((ok) => {
      toast(ok ? 'Username copied' : 'Could not copy', ok ? 'good' : 'bad');
    });
  }, [toast]);

  const build = useCallback((): MenuItem[] => {
    const isMe = me?.id === user.id;
    const status = relation?.status;
    const theyAsked = status === 'pending' && relation?.actorId === user.id;
    const blockedByMe = status === 'blocked' && relation?.actorId === me?.id;

    const items: MenuItem[] = [
      { label: 'View full profile', icon: 'profile', onClick: () => { leave?.(); go('u/' + user.username); } }
    ];

    if (isMe) {
      items.push({ label: 'Appearance and perks', icon: 'palette', onClick: () => { leave?.(); go('settings'); } });
      items.push('-');
      items.push({ label: 'Copy username', icon: 'copy', onClick: () => copy(user.username) });
      return items;
    }

    items.push({ label: 'Message', icon: 'chat', onClick: () => { leave?.(); go('chat/' + user.username); } });

    if (blockedByMe) {
      items.push('-');
      items.push({
        label: 'Unblock',
        icon: 'checkCirc',
        onClick: () => void run('/api/friends/unblock', { userId: user.id }, 'Unblocked')
      });
      return items;
    }

    if (theyAsked && relation) {
      items.push({
        label: 'Accept friend request',
        icon: 'check',
        onClick: () => void run('/api/friends/' + relation.id + '/accept', undefined, 'You are now friends')
      });
      items.push({
        label: 'Decline request',
        icon: 'close',
        onClick: () => void run('/api/friends/' + relation.id + '/decline', undefined, 'Declined')
      });
    } else if (status === 'accepted' && relation) {
      items.push({
        label: 'Remove friend',
        icon: 'close',
        onClick: () => {
          void (async () => {
            const ok = await confirm('Remove ' + user.displayName + '?', 'You can add each other again later.', {
              confirmLabel: 'Remove',
              danger: true
            });
            if (!ok) return;
            try {
              await api.del('/api/friends/' + relation.id);
              toast('Removed', 'good');
              await refresh();
            } catch (err) {
              toast((err as Error).message, 'bad');
            }
          })();
        }
      });
    } else if (status !== 'pending') {
      items.push({
        label: 'Add friend',
        icon: 'addFriend',
        onClick: () => void run('/api/friends/request', { username: user.username }, 'Friend request sent')
      });
    }

    if (status === 'accepted' && relation) {
      items.push({
        /* Something you will want in three weeks: where you met them, when
           they play. Yours alone - they are never shown it. */
        label: 'Note about them',
        icon: 'edit',
        onClick: () => setNoting(true)
      });
      items.push({
        label: 'Introduce to a friend',
        icon: 'handshake',
        onClick: () => setIntroducing(true)
      });
    }

    items.push('-');
    items.push({ label: 'Invite to party', icon: 'squads', onClick: () => setInviting(true) });

    items.push('-');
    items.push({ label: 'Copy username', icon: 'copy', onClick: () => copy(user.username) });
    items.push({ label: 'Report', icon: 'flag', onClick: () => setReporting(true) });
    items.push({
      label: 'Block',
      icon: 'ban',
      danger: true,
      onClick: () => {
        void (async () => {
          const ok = await confirm(
            'Block ' + user.displayName + '?',
            'They cannot message or invite you, and you disappear from each other.',
            { confirmLabel: 'Block', danger: true }
          );
          if (ok) void run('/api/friends/block', { userId: user.id }, 'Blocked ' + user.displayName);
        })();
      }
    });

    // Staff get the console, since that is where the levers are.
    if (me?.role === 'admin' || me?.role === 'mod') {
      items.push('-');
      items.push({
        label: 'Open in staff console',
        icon: 'shieldNav',
        onClick: () => { leave?.(); go('staff?user=' + user.username); }
      });
    }

    return items;
  }, [user, relation, me, run, confirm, leave, toast, refresh, copy]);

  const ctx = useContextMenu(build);

  const menu = (
    <>
      {ctx.menu}
      {dialog}
      {reporting ? <ReportDialog user={user} onClose={() => setReporting(false)} /> : null}
      {inviting ? <InviteToParty user={user} onClose={() => setInviting(false)} /> : null}
      {noting && relation ? (
        <FriendNote user={user} friendshipId={relation.id} onClose={() => setNoting(false)} onSaved={() => void refresh()} />
      ) : null}
      {introducing ? <Introduce user={user} onClose={() => setIntroducing(false)} /> : null}
    </>
  );

  return { props: ctx.props, openAt: ctx.openAt, close: ctx.close, menu };
}

/**
 * A private note about a friend.
 *
 * "Met in Rocket League, usually on at weekends" is the sort of thing you
 * know on the day and have forgotten by the next time they message you. It is
 * stored against your side of the friendship and never shown to them.
 */
function FriendNote({ user, friendshipId, onClose, onSaved }: {
  user: PublicUser;
  friendshipId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data } = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const row = (data?.friends ?? []).find((x) => x.id === friendshipId);
  const [note, setNote] = useState(row?.note ?? '');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    setBusy(true);
    try {
      await api.post('/api/friends/' + friendshipId + '/note', { note: note.trim(), nickname: row?.nickname ?? '' });
      toast(note.trim() ? 'Note saved' : 'Note cleared', 'good');
      onSaved();
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={'Note about ' + user.displayName}
      subtitle="Only you ever see this. They are not told it exists."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>
        </>
      }
    >
      <Field label="Your note" hint={note.length + '/120'}>
        <textarea
          autoFocus
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 120))}
          placeholder="Met in Rocket League. Usually plays weekends."
          className="w-full resize-none rounded-[var(--radius-s)] border border-line bg-surface-1 p-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
        />
      </Field>
    </Modal>
  );
}

/** Reporting somebody to the moderators. */
function ReportDialog({ user, onClose }: { user: PublicUser; onClose: () => void }) {
  const [reason, setReason] = useState<string>('harassment');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function send() {
    setBusy(true);
    try {
      await api.post('/api/reports', { userId: user.id, reason, note });
      toast('Report sent to the moderators', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={'Report ' + user.displayName}
      subtitle="Moderators see the reason, your note, and who sent it."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="danger" disabled={busy} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send report'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-[var(--gap)]">
        <Field label="What happened?">
          <Select
            lead="flag"
            search={false}
            value={reason}
            onChange={setReason}
            options={REASONS.map(([id, label]) => ({ value: id, label }))}
          />
        </Field>
        <Field label="Anything else?" hint="Optional, but it helps.">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Where it happened, what was said…"
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Inviting somebody to one of the parties you are already in. */
export function InviteToParty({ user, onClose }: { user: PublicUser; onClose: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState('');
  const { data } = useQuery({
    queryKey: ['parties', 'mine'],
    queryFn: () => endpoints.parties({ scope: 'mine' })
  });
  const parties = data?.parties ?? [];

  async function invite(partyId: string) {
    setBusy(partyId);
    try {
      await api.post('/api/parties/' + partyId + '/invite', { username: user.username });
      toast('Invited ' + user.displayName, 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy('');
    }
  }

  return (
    <Modal
      title={'Invite ' + user.displayName}
      subtitle="Pick one of your parties."
      onClose={onClose}
      footer={<Button kind="ghost" onClick={onClose}>Cancel</Button>}
    >
      {parties.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-mute">
          You are not in a party yet. Post one first.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {parties.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={!!busy}
              onClick={() => void invite(p.id)}
              className="flex items-center gap-3 rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-2.5 text-left transition-colors hover:border-accent-line hover:bg-surface-2 disabled:opacity-50"
            >
              <GameMark id={p.gameId} size={30} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold">{p.title}</span>
                <span className="block text-[12px] text-mute">{p.memberCount}/{p.size}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
